import { useCallback, useEffect, useRef } from "react";

/**
 * A custom hook to manage a debounced callback function.
 *
 * Key points:
 * - Throws if `delay` is negative.
 * - The timeout is cleared on unmount or whenever `delay` changes.
 * - Always invokes the latest version of the callback.
 * - Resets the delay timer if the function is called again before the delay elapses.
 *
 * @param callback The function to debounce.
 * @param delay The debounce delay in milliseconds (must be ≥ 0).
 * @returns A debounced version of the callback.
 */
export function useDebouncedCallback<T extends unknown[]>(
    callback: (...args: T) => void,
    delay: number
): (...args: T) => void {
    if (delay < 0) {
        throw new Error(
            `useDebouncedCallback: expected delay ≥ 0, but got ${delay}`
        );
    }

    const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const callbackRef = useRef(callback);

    // Always point to latest callback
    useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    // Whenever `delay` changes, clear any pending timeout
    // (this also handles unmount because the cleanup runs on teardown)
    const ms = delay; // already guaranteed ≥ 0
    useEffect(() => {
        return () => {
            if (timeoutIdRef.current) {
                clearTimeout(timeoutIdRef.current);
                timeoutIdRef.current = null;
            }
        };
    }, [ms]);

    // Debounced function — identity stable unless `delay` changes
    const debouncedFn = useCallback((...args: T) => {
        if (timeoutIdRef.current) {
            clearTimeout(timeoutIdRef.current);
        }
        timeoutIdRef.current = setTimeout(() => {
            callbackRef.current(...args);
        }, ms);
    }, [ms]);

    return debouncedFn;
}
